'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { PaperObject } from '@/components/piai/items/PaperObject';
import { LANDING_MONTH_ITEMS, LandingMonthPeek } from '@/components/piai/marketing/LandingMonthPeek';

function momentLine(item: (typeof LANDING_MONTH_ITEMS)[number]): string {
  if (item.cadence && item.amount && item.when) {
    return `${item.cadence} · ${item.amount} · ${item.when}`;
  }
  return item.when ? item.when : '';
}

function Reveal({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [on, setOn] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setOn(true);
      },
      { threshold: 0.45 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} className={on ? 'piai-mkt-reveal is-in' : 'piai-mkt-reveal'}>
      {children}
    </div>
  );
}

export function LandingStory() {
  return (
    <div className="piai-mkt-story">
      <Reveal>
        <h2 className="piai-display piai-mkt-h2">Your household already has a rhythm.</h2>
        <p className="piai-mkt-lead">PIAI notices it — then puts it on your month.</p>
      </Reveal>

      <ul className="piai-mkt-moments">
        {LANDING_MONTH_ITEMS.map((item) => (
          <li key={item.title}>
            <Reveal>
              <div className="piai-mkt-moment">
                <PaperObject kind={item.kind} detail="month" title={item.title} />
                <div>
                  <p className="piai-mkt-moment-title">{item.title}</p>
                  <p className="piai-mkt-moment-line">{momentLine(item)}</p>
                </div>
              </div>
            </Reveal>
          </li>
        ))}
      </ul>

      <Reveal>
        <h2 className="piai-display piai-mkt-h2">Then it lands on the month.</h2>
        <p className="piai-mkt-lead">
          Empty days stay empty. The few things you shouldn&apos;t forget become impossible to miss.
        </p>
      </Reveal>

      <LandingMonthPeek />
    </div>
  );
}
